import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import { GetFunctionsByEventId, GetGuestsByEventId, GetGuestsByEventIdForMember, UpdateGuest } from '../../api/api';
import { fullName, normaliseText } from '../../utils/helpers';

export const GUEST_TYPES = ['Family', 'Business'];

const rowFunctionIds = (row) => {
  if (row.FunctionIds) return String(row.FunctionIds).split(',').map(v => v.trim()).filter(Boolean);
  return row.FunctionId ? [String(row.FunctionId)] : [];
};

// The name printed on the invitation card:
//   Family   → the head of the family (with the courtesy prefix)
//   Business → the owner, with his company underneath
// A typed CardName always wins, so an unusual case can be written by hand.
export const cardNameOf = (row) => {
  if (String(row.CardName || '').trim()) return String(row.CardName).trim();
  const person = fullName(row) || row.GuestName || '';
  if (String(row.GuestType || 'Family') === 'Business') return person;
  return person;
};
export const cardSubtitleOf = (row) => {
  if (String(row.GuestType || 'Family') === 'Business') return String(row.CompanyName || '').trim();
  return String(row.Relation || row.GroupName || '').trim();
};

// Cards to hand out: one line per family / business associate, printable.
export function useCards(event) {
  const { user, isAdmin, can } = useAuth();
  const toast = useToast();
  const me = user.UserId;

  const canView = isAdmin || can('CanMembers');
  const canEdit = isAdmin || can('CanMembersEdit') || can('CanMembers');

  const [rows, setRows] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fnFilter, setFnFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);      // the guest whose card text is open
  const [cardForm, setCardForm] = useState({ GuestType: 'Family', CompanyName: '', CardName: '' });
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    const loader = isAdmin
      ? GetGuestsByEventId({ EventId: event.EventId })
      : GetGuestsByEventIdForMember({ EventId: event.EventId, UserId: me });
    loader.then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => toast('Failed to load guests'))
      .finally(() => setLoading(false));
  }, [event, isAdmin, me, toast]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    GetFunctionsByEventId({ EventId: event.EventId })
      .then(d => setFunctions(Array.isArray(d) ? d : [])).catch(() => setFunctions([]));
  }, [event]);

  // One card per PERSON (a family gets one card, not one per function), with the
  // functions they are invited to listed on it.
  const cards = useMemo(() => {
    const byKey = new Map();
    rows.forEach(r => {
      if (fnFilter && !rowFunctionIds(r).includes(String(fnFilter))) return;
      if (typeFilter && String(r.GuestType || 'Family') !== typeFilter) return;
      // Family members share the head's card.
      const key = Number(r.FamilyId || 0) ? `fam:${r.FamilyId}` : (r.MobileNumber ? `mob:${r.MobileNumber}` : `g:${r.GuestId}`);
      if (!byKey.has(key)) {
        byKey.set(key, { key, head: r, members: [], functionNames: new Set() });
      }
      const card = byKey.get(key);
      // The family head (GuestId === FamilyId) is the name on the card.
      if (Number(r.FamilyId || 0) && String(r.GuestId) === String(r.FamilyId)) card.head = r;
      card.members.push(r);
      String(r.FunctionNames || r.FunctionName || '').split(',').map(v => v.trim()).filter(Boolean)
        .forEach(n => card.functionNames.add(n));
    });
    let list = Array.from(byKey.values()).map(c => ({
      ...c,
      name: cardNameOf(c.head),
      subtitle: cardSubtitleOf(c.head),
      type: String(c.head.GuestType || 'Family'),
      mobile: c.head.MobileNumber || '',
      city: c.head.City || '',
      persons: c.members.reduce((s, m) => Math.max(s, Number(m.ExpectedCount || 1)), 1),
      functions: Array.from(c.functionNames),
    }));
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(c => `${c.name} ${c.subtitle} ${c.mobile} ${c.city}`.toLowerCase().includes(s));
    }
    return list.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [rows, fnFilter, typeFilter, q]);

  const counts = useMemo(() => ({
    cards: cards.length,
    family: cards.filter(c => c.type === 'Family').length,
    business: cards.filter(c => c.type === 'Business').length,
  }), [cards]);

  const openCard = (card) => {
    setEditing(card);
    setCardForm({
      GuestType: card.head.GuestType || 'Family',
      CompanyName: card.head.CompanyName || '',
      CardName: card.head.CardName || '',
    });
  };
  const setCardF = (k, v) => setCardForm(f => ({ ...f, [k]: ['CompanyName', 'CardName'].includes(k) ? normaliseText(v) : v }));

  // The card text belongs to the person, so every row of that family is updated.
  const saveCard = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const ids = editing.members.map(m => m.GuestId).join(',');
      await UpdateGuest({
        data: { ...cardForm, UpdatedBy: me },
        where: `GuestId in (${ids})`,
      });
      toast('Card updated');
      setEditing(null);
      refresh();
    } catch {
      toast('Could not save the card');
    } finally { setSaving(false); }
  };

  const print = () => window.print();
  const fnName = useCallback((id) => functions.find(f => String(f.FunctionId) === String(id))?.FunctionName || '', [functions]);

  return {
    canView, canEdit, cards, counts, loading, functions, fnFilter, setFnFilter,
    typeFilter, setTypeFilter, q, setQ, editing, setEditing, cardForm, setCardF, saveCard, saving,
    openCard, print, fnName, event,
  };
}
